import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { usePositionContext } from '../../../contexts/PositionContext';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';

interface StochasticStrategyProps {
  symbol: string;
  currentPrice?: string;
  onConfigUpdate?: (config: any) => void;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  isRunning?: boolean;
  initialConfig?: StochasticConfig; // Add prop for initial configuration
}

interface StochasticConfig {
  kPeriod: number;
  dPeriod: number;
  overboughtLevel: number;
  oversoldLevel: number;
  tradeAmountPercent: number;
  quantity: number;
  leverage: number;
  profitThreshold: number;
  lossThreshold: number;
}

interface StochasticData {
  k: number;
  d: number;
  signal: 'buy' | 'sell' | null;
}

const StochasticStrategy: React.FC<StochasticStrategyProps> = ({ 
  symbol, 
  currentPrice, 
  onConfigUpdate, 
  onSignalUpdate, 
  isRunning: externalIsRunning,
  initialConfig
}) => {
  const { theme } = useTheme();
  const { addOpenPosition, closePosition, openPositions } = usePositionContext();
  const { accountBalance, updateBalance } = useAccount();
  const { accountType } = useAccountType();

  const [config, setConfig] = useState<StochasticConfig>(() => {
    // Use initialConfig if provided, otherwise use sensible defaults
    if (initialConfig) {
      // console.log(`🔄 Stochastic: Initializing with saved config for ${symbol}:`, initialConfig);
      return {
        kPeriod: initialConfig.kPeriod || 14,
        dPeriod: initialConfig.dPeriod || 3,
        overboughtLevel: initialConfig.overboughtLevel || 80,
        oversoldLevel: initialConfig.oversoldLevel || 20,
        tradeAmountPercent: initialConfig.tradeAmountPercent || 10,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        leverage: initialConfig.leverage ?? 100,
        profitThreshold: initialConfig.profitThreshold ?? 5, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 3 // Default to $3 if not set
      };
    }
    
    // Sensible default configuration for new strategies
    return {
      kPeriod: 14,
      dPeriod: 3,
      overboughtLevel: 80,
      oversoldLevel: 20,
      tradeAmountPercent: 10,
      quantity: 0.5,         // Default quantity
      leverage: 100,
      profitThreshold: 5,   // Default profit threshold
      lossThreshold: 3      // Default loss threshold
    };
  });

  const [isRunning, setIsRunning] = useState(false);
  const [currentSignal, setCurrentSignal] = useState<'buy' | 'sell' | null>(null);
  const [lastSignal, setLastSignal] = useState<'buy' | 'sell' | null>(null);
  const [stochasticData, setStochasticData] = useState<StochasticData>({
    k: 50,
    d: 50,
    signal: null
  });
  const [hasUserEdited, setHasUserEdited] = useState(false); // Track if user has made changes

  // Use external isRunning if provided, otherwise use internal state
  const actualIsRunning = externalIsRunning !== undefined ? externalIsRunning : isRunning;

  // 🔥 CRITICAL FIX: Update config when initialConfig changes, but only if user hasn't edited
  useEffect(() => {
    if (initialConfig && !hasUserEdited) {
      // console.log(`🔄 Stochastic: Updating config from initialConfig for ${symbol}:`, initialConfig);
      setConfig({
        kPeriod: initialConfig.kPeriod || 14,
        dPeriod: initialConfig.dPeriod || 3,
        overboughtLevel: initialConfig.overboughtLevel || 80,
        oversoldLevel: initialConfig.oversoldLevel || 20,
        tradeAmountPercent: initialConfig.tradeAmountPercent || 10,
        quantity: initialConfig.quantity ?? 0.5, // Default to 0.5 if not set
        leverage: initialConfig.leverage ?? 100,
        profitThreshold: initialConfig.profitThreshold ?? 5, // Default to $5 if not set
        lossThreshold: initialConfig.lossThreshold ?? 3 // Default to $3 if not set
      });
    }
  }, [initialConfig?.quantity, initialConfig?.profitThreshold, initialConfig?.lossThreshold, initialConfig?.kPeriod, initialConfig?.dPeriod, initialConfig?.overboughtLevel, initialConfig?.oversoldLevel, initialConfig?.tradeAmountPercent, initialConfig?.leverage, symbol, hasUserEdited]);

  // Component initialization
  
  // Handle isRunning prop changes

  // Simulate Stochastic calculation (in real implementation, this would use actual market data)
  const calculateStochastic = useCallback(() => {
    if (!currentPrice || !actualIsRunning) {
      return;
    }

    // Simulate price movement and calculate Stochastic Oscillator
    const price = parseFloat(currentPrice);
    
    // Generate more realistic Stochastic values that can trigger signals more frequently
    // This is a simplified simulation - in real implementation, you'd use actual OHLC data
    const time = Date.now() / 1000; // Use time to create more predictable patterns
    const baseK = 50 + Math.sin(time * 0.2) * 50 + (Math.random() - 0.5) * 40; // Wider oscillation range to hit extremes more often
    const baseD = baseK + (Math.random() - 0.5) * 20; // D is smoothed version of K
    
    const k = Math.max(0, Math.min(100, baseK));
    const d = Math.max(0, Math.min(100, baseD));

    let signal: 'buy' | 'sell' | null = null;

    // Stochastic buy/sell signals
    // Buy signal: K is in oversold territory (≤ 20) and D is also low (≤ 25)
    if (k <= config.oversoldLevel && d <= (config.oversoldLevel + 5)) {
      signal = 'buy';
    } 
    // Sell signal: K is in overbought territory (≥ 80) and D is also high (≥ 75)
    else if (k >= config.overboughtLevel && d >= (config.overboughtLevel - 5)) {
      signal = 'sell';
    }

    setStochasticData({ k, d, signal });
    setCurrentSignal(signal);
    onSignalUpdate?.(signal);
    
    // Update signal state
    
    // Handle signal generation
    
    // Check for high values that should trigger sell signals
    
    // Check for low values that should trigger buy signals
  }, [currentPrice, actualIsRunning, config.oversoldLevel, config.overboughtLevel, onSignalUpdate]);

  // Execute AI trade based on signal
  const executeAITrade = useCallback(async (signal: 'buy' | 'sell') => {
    if (!currentPrice || !accountBalance) {
      return;
    }
    
    const price = parseFloat(currentPrice);
    const leverage = 100; // Always use 100x leverage
    
    // Calculate trade amount as 20% of balance
    const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const tradeAmountUSD = currentBalance * 0.20; // 20% of balance
    const amount = Math.max(1.10, tradeAmountUSD / price); // Minimum 1.10 quantity
    
    // Check if we have sufficient balance
    if (tradeAmountUSD > currentBalance) {
      return;
    }

    // Check for existing AI positions of the same side (Buy/Sell)
    const existingPositions = openPositions.filter(position => 
      position.isAI && 
      position.symbol === symbol && 
      position.type === (signal === 'buy' ? 'Buy' : 'Sell')
    );

    // If there's already a position of the same side, don't open a new one
    if (existingPositions.length > 0) {
      return;
    }

    // Create new position
    if (addOpenPosition) {
      const positionData = {
        symbol: symbol,
        type: (signal === 'buy' ? 'Buy' : 'Sell') as 'Buy' | 'Sell',
        size: amount.toFixed(6), // More precision for crypto amounts
        entry: price.toString(),
        current: price.toString(),
        pnl: '$0.00',
        pnlPercent: '0.00%',
        leverage: leverage,
        orderType: 'market' as const, // AI trades are always market orders
      };
      
      // Trade executed
      const newPosition = await addOpenPosition(positionData);
      
      // Deduct margin from account balance for AI trades (same as manual trades)
      if (updateBalance && accountBalance && newPosition) {
        const positionValue = amount * price;
        const marginRequired = positionValue / leverage;
        const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
        const newBalance = currentBalance - marginRequired;
        await updateBalance(accountType, newBalance);
      }
    }
  }, [currentPrice, addOpenPosition, symbol, accountBalance, accountType, config.tradeAmountPercent, config.leverage, openPositions, closePosition, updateBalance]);

  // Monitor for signal changes and execute trades
  useEffect(() => {
    if (currentSignal && currentSignal !== lastSignal) {
      executeAITrade(currentSignal);
      setLastSignal(currentSignal);
    }
  }, [currentSignal, lastSignal, executeAITrade]);

  // Calculate Stochastic periodically
  useEffect(() => {
    if (!actualIsRunning) {
      return;
    }
    
    const interval = setInterval(calculateStochastic, 5000); // Check every 5 seconds
    return () => {
      clearInterval(interval);
    };
  }, [calculateStochastic, actualIsRunning, symbol]);

  const handleConfigChange = (field: keyof StochasticConfig, value: number) => {
    setHasUserEdited(true); // Mark that user has made changes
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    onConfigUpdate?.(newConfig);
  };

  const getSignalColor = (signal: 'buy' | 'sell' | null) => {
    switch (signal) {
      case 'buy': return 'text-green-400';
      case 'sell': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getSignalBgColor = (signal: 'buy' | 'sell' | null) => {
    switch (signal) {
      case 'buy': return 'bg-green-500/20 border-green-500/30';
      case 'sell': return 'bg-red-500/20 border-red-500/30';
      default: return 'bg-gray-500/20 border-gray-500/30';
    }
  };

  // Test function to manually trigger signals for testing
  const triggerTestSignal = (signalType: 'buy' | 'sell') => {
    if (!actualIsRunning) return;
    
    // Test signal triggered
    
    // Simulate extreme values to trigger signals
    const testK = signalType === 'buy' ? 15 : 85; // Well within oversold/overbought
    const testD = signalType === 'buy' ? 18 : 82; // Well within oversold/overbought
    
    setStochasticData({ k: testK, d: testD, signal: signalType });
    setCurrentSignal(signalType);
    onSignalUpdate?.(signalType);
  };

  return (
    <div className="space-y-4">
      {/* Strategy Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium theme-text-primary">Stochastic Oscillator</h3>
          <p className="text-xs theme-text-quaternary">Momentum indicator for overbought/oversold conditions</p>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${getSignalBgColor(currentSignal)}`}>
          <span className={getSignalColor(currentSignal)}>
            {currentSignal ? currentSignal.toUpperCase() : 'NEUTRAL'}
          </span>
        </div>
      </div>

      {/* Current Stochastic Values */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-500/10 rounded-lg">
          <div className="text-xs theme-text-quaternary mb-1">%K</div>
          <div className="text-sm font-medium theme-text-primary">
            {stochasticData.k.toFixed(2)}
          </div>
        </div>
        <div className="p-3 bg-gray-500/10 rounded-lg">
          <div className="text-xs theme-text-quaternary mb-1">%D</div>
          <div className="text-sm font-medium theme-text-primary">
            {stochasticData.d.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="space-y-3">
        <div>
          <label className="block text-xs theme-text-primary mb-1">
            K Period: {config.kPeriod}
          </label>
                     <input
             type="range"
             min="5"
             max="30"
             value={config.kPeriod}
             onChange={(e) => handleConfigChange('kPeriod', parseInt(e.target.value))}
             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
           />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            D Period: {config.dPeriod}
          </label>
                     <input
             type="range"
             min="1"
             max="10"
             value={config.dPeriod}
             onChange={(e) => handleConfigChange('dPeriod', parseInt(e.target.value))}
             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
           />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Overbought Level: {config.overboughtLevel}%
          </label>
                     <input
             type="range"
             min="70"
             max="95"
             value={config.overboughtLevel}
             onChange={(e) => handleConfigChange('overboughtLevel', parseInt(e.target.value))}
             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
           />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Oversold Level: {config.oversoldLevel}%
          </label>
                     <input
             type="range"
             min="5"
             max="30"
             value={config.oversoldLevel}
             onChange={(e) => handleConfigChange('oversoldLevel', parseInt(e.target.value))}
             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
           />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Trade Amount: {config.tradeAmountPercent}% of balance
          </label>
          <div className="flex items-center space-x-1">
            <span className="text-xs theme-text-quaternary">%</span>
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseInt(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="50"
              step="1"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Quantity: {config.quantity}
          </label>
          <input
            type="number"
            value={config.quantity}
            onChange={(e) => handleConfigChange('quantity', parseFloat(e.target.value))}
            className="w-full px-2 py-1 text-xs bg-transparent border theme-border rounded theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="0.01"
            max="1000"
            step="0.01"
          />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Leverage: {config.leverage}x
          </label>
                     <input
             type="range"
             min="1"
             max="20"
             value={config.leverage}
             onChange={(e) => handleConfigChange('leverage', parseInt(e.target.value))}
             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
           />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Profit Threshold: ${config.profitThreshold}
          </label>
          <input
            type="number"
            value={config.profitThreshold}
            onChange={(e) => handleConfigChange('profitThreshold', parseFloat(e.target.value))}
            className="w-full px-2 py-1 text-xs bg-transparent border theme-border rounded theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="0.01"
            max="100"
            step="0.01"
          />
        </div>

        <div>
          <label className="block text-xs theme-text-primary mb-1">
            Loss Threshold: ${config.lossThreshold}
          </label>
          <input
            type="number"
            value={config.lossThreshold}
            onChange={(e) => handleConfigChange('lossThreshold', parseFloat(e.target.value))}
            className="w-full px-2 py-1 text-xs bg-transparent border theme-border rounded theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            min="0.01"
            max="100"
            step="0.01"
          />
        </div>
      </div>

      {/* Strategy Status */}
      <div className="p-3 bg-gray-500/10 rounded-lg">
        <div className="text-xs theme-text-quaternary mb-1">Strategy Status</div>
        <div className="text-sm theme-text-primary">
          {actualIsRunning ? 'Monitoring for signals...' : 'Strategy inactive'}
        </div>
                 {actualIsRunning && (
           <div className="text-xs theme-text-quaternary mt-1">
             Looking for %K &lt;= {config.oversoldLevel}% AND %D &lt;= {config.oversoldLevel + 5}% (Buy) or %K &gt;= {config.overboughtLevel}% AND %D &gt;= {config.overboughtLevel - 5}% (Sell)
           </div>
         )}
      </div>

      {/* Test Buttons for Manual Signal Testing */}
      {actualIsRunning && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="text-xs font-medium text-yellow-400 mb-2">Test Signals:</div>
          <div className="flex space-x-2">
            <button
              onClick={() => triggerTestSignal('buy')}
              className="flex-1 py-1 px-2 bg-green-500/20 text-green-400 text-xs rounded hover:bg-green-500/30 transition-colors"
            >
              Test Buy Signal
            </button>
            <button
              onClick={() => triggerTestSignal('sell')}
              className="flex-1 py-1 px-2 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30 transition-colors"
            >
              Test Sell Signal
            </button>
          </div>
        </div>
      )}

      {/* Signal Explanation */}
      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <div className="text-xs font-medium text-blue-400 mb-2">How it works:</div>
                         <div className="text-xs theme-text-quaternary space-y-1">
          <div>• <span className="text-green-400">Buy Signal:</span> %K &lt;= {config.oversoldLevel}% AND %D &lt;= {config.oversoldLevel + 5}% (oversold)</div>
          <div>• <span className="text-red-400">Sell Signal:</span> %K &gt;= {config.overboughtLevel}% AND %D &gt;= {config.overboughtLevel - 5}% (overbought)</div>
          <div>• Automatically closes existing position before opening new one</div>
          <div>• Uses {config.tradeAmountPercent}% of available balance per trade</div>
        </div>
      </div>
    </div>
  );
};

export default StochasticStrategy;
